import React from 'react';
import { useFormField } from '../../core';
import { useFetchExternalLookup } from '@/hooks';
import { SelectOption } from '../../typings';
import { Dropdown, Icon } from '@/ui-kit';
import {
  DropdownDatalistOption,
  type DropdownInputMode,
} from '@/ui-kit/dropdown/dropdown';
import { config } from '../../config';
import {
  ADDRESS_LOOKUP_TYPES,
  allowsPoBox,
  FACILITY_NAME_MAX_LENGTH,
  getPoBoxModeHint,
  getTypeaheadHintText,
  isFacilityLookup,
  poBoxInputMode,
  resolveTypeaheadHintState,
} from '@/forms/utils';

type SelectProps = {
  label: string;
  name: string;
  placeholder?: string;
  options?: SelectOption[];
  /** Caps filter/search typing to match MaxLength validation (e.g. state abbreviations). */
  maxSearchLength?: number;
  /** Shown under the dropdown while it has no error, e.g. why it offers nothing. */
  noOptionsText?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [x: string]: any;
};

export const Select: React.FC<SelectProps> = ({
  label,
  maxSearchLength,
  noOptionsText,
  ...props
}) => {
  const [field, meta] = useFormField(props);
  const { value, onChange, ...fieldProps } = field;
  const [searchValue, setSearchValue] = React.useState<string>('');

  const { lookup } = useFetchExternalLookup(props.name, searchValue);

  const [datalist, setDatalist] = React.useState<DropdownDatalistOption[]>([]);
  const [valueLabel, setValueLabel] = React.useState<string>('');
  // The last facility pick's name, so its label survives a search that no longer lists it.
  const pickedRef = React.useRef<{ key: string; name: string } | null>(null);
  // The mode the last revert happened in; cleared on the next focus or edit.
  const [revertMode, setRevertMode] = React.useState<DropdownInputMode | null>(
    null,
  );
  const [inputMode, setInputMode] = React.useState<{
    mode: DropdownInputMode;
    live: boolean;
  }>({ mode: 'search', live: false });

  const error = meta.touched ? meta.error : undefined;

  const isTypeaheadSingle = Boolean(lookup) && !props.multiple;
  const lookupType = lookup?.options?.lookupType;
  const isAddressTypeahead =
    isTypeaheadSingle &&
    lookupType !== undefined &&
    ADDRESS_LOOKUP_TYPES.has(lookupType);
  const allowPoBox =
    isAddressTypeahead &&
    allowsPoBox(lookup?.options?.additionalLookupArguments);
  const isFacilityTypeahead =
    isTypeaheadSingle && isFacilityLookup(lookup?.options);
  // A kept facility name is saved as typed, so the input stops at the server's cap.
  const inputMaxLength = isFacilityTypeahead
    ? FACILITY_NAME_MAX_LENGTH
    : maxSearchLength;
  const { defaultHint, revertWarning } = getTypeaheadHintText(
    lookupType,
    allowPoBox,
  );

  const getInputMode = React.useCallback(
    (text: string) => poBoxInputMode(text, allowPoBox),
    [allowPoBox],
  );
  const onInputModeChange = React.useCallback(
    (mode: DropdownInputMode, live: boolean) =>
      // Keeps the same object for a repeated report, so React skips the re-render.
      setInputMode(prev =>
        prev.mode === mode && prev.live === live ? prev : { mode, live },
      ),
    [],
  );

  const hintState: { state: 'default' | 'error' | 'warning'; stateText: string | undefined } =
    isTypeaheadSingle
      ? resolveTypeaheadHintState({
          error,
          liveModeHint: inputMode.live
            ? getPoBoxModeHint(inputMode.mode, allowPoBox)
            : null,
          revertModeHint: revertMode
            ? getPoBoxModeHint(revertMode, allowPoBox)
            : null,
          showRevertWarning: revertMode !== null,
          // Read from the shown value, so it needs no first report from the Dropdown.
          savedModeHint:
            !inputMode.live && getInputMode(valueLabel) === 'free'
              ? getPoBoxModeHint('free', allowPoBox)
              : null,
          defaultHint,
          revertWarning,
        })
      : { state: error ? 'error' : 'default', stateText: error ?? noOptionsText };

  // Cast value to expected type for type safety
  const fieldValue = value as string | string[] | undefined;

  React.useEffect(() => {
    const isExternalLookupValue =
      fieldValue !== undefined && fieldValue !== null
        ? String(fieldValue).startsWith(config.externalLookupPrefix)
        : false;

    if (isExternalLookupValue) {
      const key = String(fieldValue);
      const picked = pickedRef.current;
      setValueLabel(
        lookup?.values?.find(
          option => option.id === key.replace(config.externalLookupPrefix, ''),
        )?.name ??
          (picked?.key === key ? picked.name : ''),
      );
    } else if (fieldValue !== undefined && fieldValue !== null) {
      setValueLabel(String(fieldValue));
    }

    const suggestedOptions =
      lookup && (fieldValue === undefined || fieldValue === '' || searchValue !== '')
        ? lookup?.values?.map(option => {
            return {
              value: option.name,
              selected: false,
              disabled: false,
              key: `${config.externalLookupPrefix}${option.id}`,
            };
          }) ?? []
        : props.options?.map(option => {
            const isMultiString =
              typeof fieldValue === 'string' &&
              fieldValue.includes(config.multiValueDivider);

            const selected =
              typeof fieldValue === 'string'
                ? isMultiString
                  ? fieldValue
                      .split(config.multiValueDivider)
                      .includes(option.value.toString())
                  : fieldValue === option.value
                : Array.isArray(fieldValue) && fieldValue.includes(option.value as string);

            if (selected && !props.multiple) {
              setValueLabel(option.label);
            }

            return {
              value: option.label,
              selected,
              disabled: false,
              key: option.value.toString(),
            };
          }) ?? [];

    setDatalist(suggestedOptions);
  }, [lookup, props.options, props.multiple, fieldValue, searchValue]);

  return (
    <Dropdown
      onToggle={() => {}}
      label={label}
      id={fieldProps.name}
      {...(inputMaxLength != null ? { maxLength: inputMaxLength } : {})}
      {...(lookup ? { leadingIcon: <Icon type="search" /> } : {})}
      as={lookup ? 'search' : 'select'}
      variant={props.multiple ? 'multi' : 'simple'}
      datalist={{ options: datalist }}
      onChange={e => {
        if (e.target.value.startsWith(config.searchPrefix)) {
          const raw = e.target.value.replace(config.searchPrefix, '');
          const searchTerm =
            inputMaxLength != null ? raw.slice(0, inputMaxLength) : raw;
          setSearchValue(searchTerm);
        } else {
          setRevertMode(null);
          if (e.target.value === '') {
            setValueLabel('');
            if (lookup) {
              setSearchValue('');
            } else {
              setDatalist(
                datalist.map(option => ({ ...option, selected: false })),
              );
            }
          } else if (
            lookup ||
            e.target.value.includes(config.internalLookupPrefix)
          ) {
            const values = e.target.value.split(config.multiValueDivider);
            const selected = values
              .map(value => datalist.find(option => option.key === value))
              .filter((o): o is DropdownDatalistOption => o !== undefined);
            const selectedOptions = selected.map(o => o.key);

            if (selectedOptions.length > 0) {
              if (!props.multiple) {
                // Only a facility keeps typed text, so only its pick's name must outlive a search.
                if (isFacilityTypeahead) {
                  pickedRef.current = {
                    key: selected[0].key,
                    name: selected[0].value,
                  };
                }
                setValueLabel(selectedOptions[0]);
              }
              const mappedDatalist = datalist.map(option => ({
                ...option,
                selected: selectedOptions.includes(option.key),
              }));
              setDatalist(mappedDatalist);
            }
          } else {
            setValueLabel(e.target.value.replace(config.customValuePrefix, ''));
          }

          onChange(e);
        }
      }}
      value={valueLabel}
      state={hintState.state}
      stateText={hintState.stateText}
      onInvalidEdit={mode => setRevertMode(mode)}
      onFocus={() => setRevertMode(null)}
      {...(isAddressTypeahead ? { getInputMode, onInputModeChange } : {})}
      keepsTypedText={isFacilityTypeahead}
      canAddCustom={props.canAddCustom}
      {...fieldProps}
      {...props}
    />
  );
};
